/**
 * Automation Card
 *
 * Base composition component for all automation types.
 * Matches v2 layout with inline editing per section.
 *
 * Architecture:
 * - When section: TimingSection + TriggerSelect + TriggerComponent
 * - Who section: AutomationWhoSection
 * - What section: Provided by action config
 * - Message section: Simple text field
 * - How section: AutomationMediaSection
 */

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { useFormikContext } from 'formik';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card, CardContent } from 'src/components/Card';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';

import { getAutomationAction, getAutomationActionLabel, resolveActionTimingConfig, shouldShowWhatSection } from './automationActions';
import {
  getUnknownVariables,
  getVarsForTrigger,
} from './automationMessageVariables';
import { TRIGGERS } from './automationTriggers';
import {
  EditableSection,
  MessageDisclaimer,
  TimingSection,
  TitleRow,
  TriggerSelect,
  WhoSummary,
} from './components';
import { useAutomationMedia } from './hooks/useAutomationMedia';
import { AutomationMediaSection } from './sections/AutomationMediaSection';
import { AutomationWhoSection } from './sections/AutomationWhoSection';
import { SendToSection, SendToSummary } from './sections/SendToSection';
import { AutomationItem } from './types';

export interface AutomationCardProps {
  /** The automation item being configured */
  automation: AutomationItem;
  /** Index in the automations array (for formik field paths) */
  index: number;
  /** Callback when automation is removed */
  onRemove?: () => void;
  /** Callback when automation is cloned */
  onClone?: () => void;
  /** Render function for the action-specific "WHAT" section */
  renderWhatSection: () => ReactNode;
  /** Render function for the "WHAT" section summary (collapsed view) */
  renderWhatSummary?: () => ReactNode;
  /** Whether to show the media section (default: true) */
  showMediaSection?: boolean;
  /** Whether to show the filters/who section as editable (default: true) */
  showFiltersSection?: boolean;
  /** Whether to show the message section (default: true) */
  showMessageSection?: boolean;
  /** Whether to show the send to section (default: false) */
  showSendToSection?: boolean;
  /** Placeholder text for send to section */
  sendToPlaceholder?: string;
  /** Optional custom render for the WHEN section (overrides default timing + trigger) */
  renderWhenSection?: () => ReactNode;
  /** Optional custom render for the WHEN summary (overrides default) */
  renderWhenSummaryOverride?: () => ReactNode;
  /** External reset key - when changed, resets all sections to "done editing" */
  resetKey?: number;
  /** Persist this card’s form values (single automation create/update) */
  onSaveCard?: () => void | Promise<void>;
  /** Revert this card to the last loaded values, or remove unsaved new automations */
  onDiscardCard?: () => void;
  /** True while this card’s save request is in flight */
  isSavingCard?: boolean;
}

export const AutomationCard: FC<AutomationCardProps> = ({
  automation,
  index,
  onRemove,
  onClone,
  renderWhatSection,
  renderWhatSummary,
  showMediaSection = true,
  showFiltersSection = true,
  showMessageSection = true,
  showSendToSection = false,
  sendToPlaceholder,
  renderWhenSection,
  renderWhenSummaryOverride,
  resetKey,
  onSaveCard,
  onDiscardCard,
  isSavingCard,
}) => {
  const { t } = useTranslation();
  const [editingName, setEditingName] = useState(false);
  // Key to reset all EditableSection components (incremented on Save All)
  const [sectionResetKey, setSectionResetKey] = useState(0);
  /** Saved automations: hide Save/Discard until the user edits this card (new drafts always show actions). */
  const [cardInteracted, setCardInteracted] = useState(!automation.id);
  const { setFieldValue } = useFormikContext<{
    automations: AutomationItem[];
  }>();

  // Reset sections when external resetKey changes (e.g., Save All)
  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      setEditingName(false);
      setSectionResetKey((k) => k + 1);
      setCardInteracted(!automation.id);
    }
  }, [resetKey, automation.id]);

  const actionConfig = getAutomationAction(automation.customType);
  const actionLabel = getAutomationActionLabel(automation.customType, t);
  const availableTriggers = actionConfig?.triggers || [];

  // Get media configuration for this automation
  const { mediaList } = useAutomationMedia(automation);

  // Sellable types that can be selected from SellableTypeSelect
  const SELLABLE_TYPES = ['workout_plan', 'group', 'product', 'package'];

  // Get the trigger config and component for current trigger
  const triggerKey = automation.reminderType || '';

  // Determine which trigger config to use
  // Special case: if reminderType is a sellable type (e.g., 'group') and the action
  // supports 'some' trigger (purchased specific), use the 'some' config to maintain
  // the PurchasedSpecificTrigger behavior with type dropdown + items multi-select
  let effectiveTriggerKey = triggerKey;
  if (SELLABLE_TYPES.includes(triggerKey) && availableTriggers.includes('some')) {
    effectiveTriggerKey = 'some';
  }

  const triggerConfig = effectiveTriggerKey ? TRIGGERS[effectiveTriggerKey] : null;
  const TriggerComponent = triggerConfig?.Component;

  // Get message content
  const messageContent =
    Array.isArray(automation.content) && automation.content.length > 0
      ? automation.content[0]
      : '';

  const validVariables = getVarsForTrigger(automation.reminderType || '');

  const getVariableError = (text: string): string | undefined => {
    const unknown = getUnknownVariables(text, validVariables);
    if (unknown.length === 1) {
      return t('automations.common.variable.unknownToken', { token: unknown[0] });
    }
    if (unknown.length > 1) {
      return t('automations.common.variable.unknownTokens', {
        tokens: unknown.join(', '),
      });
    }
    return undefined;
  };

  const subjectVariableError = getVariableError(automation.subject || '');
  const messageVariableError = getVariableError(messageContent);

  const hasEmail =
    Array.isArray(automation.media) &&
    automation.media.includes('email');

  const showEmailSubject =
    hasEmail &&
    !['upcoming-appointment', 'workout-today', 'visits-left'].includes(
      automation.customType || ''
    );

  const showWhat = shouldShowWhatSection(actionConfig, automation);
  const whatEditable = actionConfig?.whatEditable !== false;

  const whyContent =
    automation.customType === 'close-out-visit'
      ? t('automations.visitCloseOut.why')
      : automation.customType === 'upcoming-appointment'
        ? t('automations.upcomingAppointment.why', {
            client: t('trainer.client.one').toLowerCase(),
          })
        : null;

  const whoFilterTypes =
    automation.customType === 'upcoming-appointment'
      ? (['tag', 'group', 'location'] as const)
      : undefined;

  // Check if this is a recurring automation (no event triggers)
  // Only treat as recurring if action config exists AND explicitly has no triggers
  const isRecurring = actionConfig !== null && availableTriggers.length === 0;

  // Some triggers hide the "Who?" section (e.g., low-stock-threshold sends to staff, not clients)
  const hideWhoSection = triggerConfig?.hideWhoSection ?? false;

  const timingAfterTrigger = triggerConfig?.timingAfterTrigger ?? false;
  const whenTimingConfig = resolveActionTimingConfig(
    actionConfig,
    triggerKey,
    triggerConfig
      ? {
          ...triggerConfig.timingConfig,
          timingAfterTrigger: triggerConfig.timingAfterTrigger,
        }
      : undefined
  );

  const renderTriggerSummary = () =>
    TriggerComponent ? (
      <TriggerComponent automation={automation} index={index} readOnly />
    ) : (
      <span>{triggerConfig?.label || triggerKey}</span>
    );

  // Render the When section summary (readonly mode)
  const renderWhenSummary = () => {
    // For recurring automations, just show the timing
    if (isRecurring) {
      return (
        <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="baseline">
          <TimingSection
            automation={automation}
            index={index}
            config={{ delayTypes: ['day', 'week', 'month'], hideHourOfDay: actionConfig?.hideHourOfDay }}
            readOnly
            recurring
          />
        </Box>
      );
    }

    if (!triggerKey) {
      return (
        <span style={{ fontStyle: 'italic', color: 'text.secondary' }}>
          {t('automations.common.selectTrigger')}
        </span>
      );
    }

    return (
      <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="baseline">
        {timingAfterTrigger ? (
          <>
            {renderTriggerSummary()}
            <TimingSection
              automation={automation}
              index={index}
              config={whenTimingConfig}
              readOnly
            />
          </>
        ) : (
          <>
            <TimingSection
              automation={automation}
              index={index}
              config={whenTimingConfig}
              readOnly
            />
            {renderTriggerSummary()}
          </>
        )}
      </Box>
    );
  };

  // Render the When section editor
  const renderWhenEditor = () => (
    <Stack spacing={2}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        {timingAfterTrigger && !isRecurring && (
          <TriggerSelect index={index} availableTriggers={availableTriggers} />
        )}
        {!timingAfterTrigger && (
          <TimingSection
            automation={automation}
            index={index}
            config={
              isRecurring
                ? {
                    delayTypes: ['day', 'week', 'month'],
                    hideHourOfDay: actionConfig?.hideHourOfDay,
                  }
                : whenTimingConfig
            }
            readOnly={false}
            recurring={isRecurring}
          />
        )}
        {timingAfterTrigger && (
          <TimingSection
            automation={automation}
            index={index}
            config={whenTimingConfig}
            readOnly={false}
          />
        )}
        {/* Only show trigger select if there are triggers available */}
        {!isRecurring && !timingAfterTrigger && (
          <TriggerSelect index={index} availableTriggers={availableTriggers} />
        )}
      </Box>
      {!isRecurring && TriggerComponent ? (
        <TriggerComponent
          automation={automation}
          index={index}
          readOnly={false}
        />
      ) : !isRecurring && triggerKey ? (
        <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {t('automations.common.triggerNotFound', { trigger: triggerKey })}
        </Box>
      ) : !isRecurring ? (
        <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {t('automations.common.selectTriggerToConfigure')}
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <Card>
      <CardContent>
        <Stack spacing={1}>
          {/* Title Row: Name | Action Type [icons] */}
          <TitleRow
            name={automation.name}
            actionLabel={actionLabel}
            automationId={automation.id}
            createdAt={automation.createdAt}
            updatedAt={automation.updatedAt}
            editing={editingName}
            onEditStart={() => {
              setCardInteracted(true);
              setEditingName(true);
            }}
            onEditEnd={() => setEditingName(false)}
            onNameChange={(name) => {
              setCardInteracted(true);
              setFieldValue(`automations.${index}.name`, name);
            }}
            onRemove={onRemove}
            onClone={onClone}
          />

          {/* WHEN Section - Uses trigger components or custom override */}
          <EditableSection
            key={`when-${sectionResetKey}`}
            label={t('automations.sections.when')}
            defaultEditing={!automation.id}
            summary={renderWhenSummaryOverride ? renderWhenSummaryOverride() : renderWhenSummary()}
            editor={renderWhenSection ? renderWhenSection() : renderWhenEditor()}
            onEditStart={() => setCardInteracted(true)}
          />

          {/* WHO Section - hidden for triggers that send to staff (e.g., low-stock-threshold) */}
          {!hideWhoSection && (
            <EditableSection
              key={`who-${sectionResetKey}`}
              label={t('automations.sections.who')}
              editable={showFiltersSection}
              summary={<WhoSummary automation={automation} />}
              editor={
                <AutomationWhoSection
                  automation={automation}
                  index={index}
                  allowedFilterTypes={whoFilterTypes}
                />
              }
              onEditStart={() => setCardInteracted(true)}
            />
          )}

          {/* SEND TO Section - shown for actions that support recipient selection */}
          {showSendToSection && (
            <EditableSection
              key={`sendTo-${sectionResetKey}`}
              label={t('automations.sections.sendTo')}
              summary={
                <SendToSummary
                  automation={automation}
                  placeholder={sendToPlaceholder}
                />
              }
              editor={
                <SendToSection
                  automation={automation}
                  index={index}
                  placeholder={sendToPlaceholder}
                />
              }
              onEditStart={() => setCardInteracted(true)}
            />
          )}

          {whyContent && (
            <Box display="flex" gap={1} alignItems="baseline">
              <Box sx={{ width: 80, flexShrink: 0 }}>
                <Typography variant="body2" fontWeight="bold">
                  {t('automations.sections.why')}
                </Typography>
              </Box>
              <Typography variant="body2">{whyContent}</Typography>
            </Box>
          )}

          {/* WHAT Section */}
          {showWhat && (
            <EditableSection
              key={`what-${sectionResetKey}`}
              label={t('automations.sections.what')}
              defaultEditing={!automation.id && automation.customType === 'client-tag'}
              editable={whatEditable}
              summary={renderWhatSummary?.() ?? t('automations.common.notConfigured')}
              editor={renderWhatSection()}
              onEditStart={() => setCardInteracted(true)}
            />
          )}

          {/* Message Section */}
          {showMessageSection && (
            <EditableSection
              key={`message-${sectionResetKey}`}
              label={t('automations.sections.message')}
              onEditStart={() => setCardInteracted(true)}
              summary={
                <Box>
                  {showEmailSubject && automation.subject && (
                    <Box>
                      <strong>{t('automations.common.subject')}:</strong>{' '}
                      {automation.subject}
                    </Box>
                  )}
                  <span
                    style={{
                      fontStyle: messageContent ? 'normal' : 'italic',
                    }}
                  >
                    {messageContent || t('automations.common.enterMessage')}
                  </span>
                </Box>
              }
              editor={
                <Stack spacing={2}>
                  {showEmailSubject && (
                    <TextField
                      value={automation.subject || ''}
                      onChange={(e) => {
                        setFieldValue(
                          `automations.${index}.subject`,
                          e.target.value
                        );
                      }}
                      label={t('automations.common.emailSubject')}
                      placeholder={t('automations.common.emailSubject')}
                      fullWidth
                      error={!!subjectVariableError}
                      helperText={subjectVariableError}
                    />
                  )}
                  {automation.customType === 'upcoming-appointment' && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        {t('automations.upcomingAppointment.defaultMessage')}
                      </Typography>
                      <Tooltip title={t('automations.upcomingAppointment.defaultMessageHelp')}>
                        <Icon icon={faCircleQuestion} size="tiny" />
                      </Tooltip>
                    </Box>
                  )}
                  <TextField
                    value={messageContent}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue(
                        `automations.${index}.content`,
                        value ? [value] : []
                      );
                    }}
                    placeholder={t('automations.common.writeYourMessage')}
                    multiline
                    rows={4}
                    fullWidth
                    error={!!messageVariableError}
                    helperText={messageVariableError}
                  />
                  <MessageDisclaimer
                    automation={automation}
                    hasDefaultMessage={automation.customType === 'upcoming-appointment'}
                  />
                </Stack>
              }
            />
          )}

          {/* HOW Section (Media/Delivery) */}
          {showMediaSection && (
            <EditableSection
              key={`how-${sectionResetKey}`}
              label={t('automations.sections.how')}
              onEditStart={() => setCardInteracted(true)}
              summary={
                automation.media?.length > 0
                  ? mediaList
                  : t('automations.common.noDeliveryMethod')
              }
              editor={
                <AutomationMediaSection automation={automation} index={index} />
              }
            />
          )}

          {onSaveCard && onDiscardCard && cardInteracted && (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
              pt={1}
            >
              <Typography
                variant="body2"
                color="primary"
                component="button"
                type="button"
                onClick={onDiscardCard}
                sx={{
                  cursor: 'pointer',
                  border: 0,
                  background: 'none',
                  font: 'inherit',
                  p: 0,
                  textDecoration: 'none',
                }}
              >
                {t('automations.page.discardChanges')}
              </Typography>
              <Button
                type="button"
                variant="prominent"
                loading={isSavingCard}
                onClick={onSaveCard}
              >
                {t('automations.page.save')}
              </Button>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
